import _defineProperty from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _classCallCheck from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/createClass.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { uuid4, dateTimestampInSeconds, consoleSandbox, logger, getGlobalObject, getGlobalSingleton, isNodeEnv } from '@sentry/utils';
import { Scope } from './scope.js';
import { closeSession, makeSession, updateSession } from './session.js';
/**
 * API compatibility version of this hub.
 *
 * WARNING: This number should only be increased when the global interface
 * changes and new methods are introduced.
 *
 * @hidden
 */

var API_VERSION = 4;
/**
 * Default maximum number of breadcrumbs added to an event. Can be overwritten
 * with {@link Options.maxBreadcrumbs}.
 */

var DEFAULT_BREADCRUMBS = 100;
/**
 * A layer in the process stack.
 * @hidden
 */

/**
 * @inheritDoc
 */

var Hub = /*#__PURE__*/function () {
  /** Contains the last event id of a captured event.  */

  /**
   * Creates a new instance of the hub, will push one {@link Layer} into the
   * internal stack on creation.
   *
   * @param client bound to the hub.
   * @param scope bound to the hub.
   * @param version number, higher number means higher priority.
   */
  function Hub(client) {
    var scope = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Scope();

    var _version = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : API_VERSION;

    _classCallCheck(this, Hub);

    ;
    this._version = _version;

    Hub.prototype.__init.call(this);

    this.getStackTop().scope = scope;

    if (client) {
      this.bindClient(client);
    }
  }
  /**
   * @inheritDoc
   */


  _createClass(Hub, [{
    key: "__init",
    value:
    /** Is a {@link Layer}[] containing the client and scope */
    function __init() {
      this._stack = [{}];
    }
  }, {
    key: "isOlderThan",
    value: function isOlderThan(version) {
      return this._version < version;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "bindClient",
    value: function bindClient(client) {
      var top = this.getStackTop();
      top.client = client;

      if (client && client.setupIntegrations) {
        client.setupIntegrations();
      }
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "pushScope",
    value: function pushScope() {
      // We want to clone the content of prev scope
      var scope = Scope.clone(this.getScope());
      this.getStack().push({
        client: this.getClient(),
        scope: scope
      });
      return scope;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "popScope",
    value: function popScope() {
      if (this.getStack().length <= 1) return false;
      return !!this.getStack().pop();
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "withScope",
    value: function withScope(callback) {
      var scope = this.pushScope();

      try {
        callback(scope);
      } finally {
        this.popScope();
      }
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "getClient",
    value: function getClient() {
      return this.getStackTop().client;
    }
    /** Returns the scope of the top stack. */

  }, {
    key: "getScope",
    value: function getScope() {
      return this.getStackTop().scope;
    }
    /** Returns the scope stack for domains or the process. */

  }, {
    key: "getStack",
    value: function getStack() {
      return this._stack;
    }
    /** Returns the topmost scope layer in the order domain > local > process. */

  }, {
    key: "getStackTop",
    value: function getStackTop() {
      return this._stack[this._stack.length - 1];
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "captureException",
    value: function captureException(exception, hint) {
      var eventId = this._lastEventId = hint && hint.event_id ? hint.event_id : uuid4();
      var syntheticException = new Error('Sentry syntheticException');

      this._withClient(function (client, scope) {
        client.captureException(exception, _objectSpread(_objectSpread({
          originalException: exception,
          syntheticException: syntheticException
        }, hint), {}, {
          event_id: eventId
        }), scope);
      });

      return eventId;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "captureMessage",
    value: function captureMessage(message, level, hint) {
      var eventId = this._lastEventId = hint && hint.event_id ? hint.event_id : uuid4();
      var syntheticException = new Error(message);

      this._withClient(function (client, scope) {
        client.captureMessage(message, level, _objectSpread(_objectSpread({
          originalException: message,
          syntheticException: syntheticException
        }, hint), {}, {
          event_id: eventId
        }), scope);
      });

      return eventId;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "captureEvent",
    value: function captureEvent(event, hint) {
      var eventId = hint && hint.event_id ? hint.event_id : uuid4();

      if (event.type !== 'transaction') {
        this._lastEventId = eventId;
      }

      this._withClient(function (client, scope) {
        client.captureEvent(event, _objectSpread(_objectSpread({}, hint), {}, {
          event_id: eventId
        }), scope);
      });

      return eventId;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "lastEventId",
    value: function lastEventId() {
      return this._lastEventId;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "addBreadcrumb",
    value: function addBreadcrumb(breadcrumb, hint) {
      var _this$getStackTop = this.getStackTop(),
          scope = _this$getStackTop.scope,
          client = _this$getStackTop.client;

      if (!scope || !client) return;

      var _ref = client.getOptions && client.getOptions() || {},
          _ref$beforeBreadcrumb = _ref.beforeBreadcrumb,
          beforeBreadcrumb = _ref$beforeBreadcrumb === void 0 ? null : _ref$beforeBreadcrumb,
          _ref$maxBreadcrumbs = _ref.maxBreadcrumbs,
          maxBreadcrumbs = _ref$maxBreadcrumbs === void 0 ? DEFAULT_BREADCRUMBS : _ref$maxBreadcrumbs;

      if (maxBreadcrumbs <= 0) return;
      var timestamp = dateTimestampInSeconds();

      var mergedBreadcrumb = _objectSpread({
        timestamp: timestamp
      }, breadcrumb);

      var finalBreadcrumb = beforeBreadcrumb ? consoleSandbox(function () {
        return beforeBreadcrumb(mergedBreadcrumb, hint);
      }) : mergedBreadcrumb;
      if (finalBreadcrumb === null) return;
      scope.addBreadcrumb(finalBreadcrumb, maxBreadcrumbs);
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setUser",
    value: function setUser(user) {
      var scope = this.getScope();
      if (scope) scope.setUser(user);
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setTags",
    value: function setTags(tags) {
      var scope = this.getScope();
      if (scope) scope.setTags(tags);
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setExtras",
    value: function setExtras(extras) {
      var scope = this.getScope();
      if (scope) scope.setExtras(extras);
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setTag",
    value: function setTag(key, value) {
      var scope = this.getScope();
      if (scope) scope.setTag(key, value);
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setExtra",
    value: function setExtra(key, extra) {
      var scope = this.getScope();
      if (scope) scope.setExtra(key, extra);
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setContext",
    value: function setContext(name, context) {
      var scope = this.getScope();
      if (scope) scope.setContext(name, context);
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "configureScope",
    value: function configureScope(callback) {
      var _this$getStackTop2 = this.getStackTop(),
          scope = _this$getStackTop2.scope,
          client = _this$getStackTop2.client;

      if (scope && client) {
        callback(scope);
      }
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "run",
    value: function run(callback) {
      var oldHub = makeMain(this);

      try {
        callback(this);
      } finally {
        makeMain(oldHub);
      }
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "getIntegration",
    value: function getIntegration(integration) {
      var client = this.getClient();
      if (!client) return null;

      try {
        return client.getIntegration(integration);
      } catch (_oO) {
        (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.warn("Cannot retrieve integration ".concat(integration.id, " from the current Hub"));
        return null;
      }
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "startTransaction",
    value: function startTransaction(context, customSamplingContext) {
      return this._callExtensionMethod('startTransaction', context, customSamplingContext);
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "traceHeaders",
    value: function traceHeaders() {
      return this._callExtensionMethod('traceHeaders');
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "captureSession",
    value: function captureSession() {
      var endSession = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

      // both send the update and pull the session from the scope
      if (endSession) {
        return this.endSession();
      } // only send the update


      this._sendSessionUpdate();
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "endSession",
    value: function endSession() {
      var layer = this.getStackTop();
      var scope = layer && layer.scope;
      var session = scope && scope.getSession();

      if (session) {
        closeSession(session);
      }

      this._sendSessionUpdate(); // the session is over; take it off of the scope


      if (scope) {
        scope.setSession();
      }
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "startSession",
    value: function startSession(context) {
      var _this$getStackTop3 = this.getStackTop(),
          scope = _this$getStackTop3.scope,
          client = _this$getStackTop3.client;

      var _ref2 = client && client.getOptions() || {},
          release = _ref2.release,
          environment = _ref2.environment; // Will fetch userAgent if called from browser sdk


      var global = getGlobalObject();

      var _ref3 = global.navigator || {},
          userAgent = _ref3.userAgent;

      var session = makeSession(_objectSpread(_objectSpread(_objectSpread({
        release: release,
        environment: environment
      }, scope && {
        user: scope.getUser()
      }), userAgent && {
        userAgent: userAgent
      }), context));

      if (scope) {
        // End existing session if there's one
        var currentSession = scope.getSession && scope.getSession();

        if (currentSession && currentSession.status === 'ok') {
          updateSession(currentSession, {
            status: 'exited'
          });
        }

        this.endSession(); // Afterwards we set the new session on the scope

        scope.setSession(session);
      }

      return session;
    }
    /**
     * Returns if default PII should be sent to Sentry and propagated in ourgoing requests
     * when Tracing is used.
     */

  }, {
    key: "shouldSendDefaultPii",
    value: function shouldSendDefaultPii() {
      var client = this.getClient();
      var options = client && client.getOptions();
      return Boolean(options && options.sendDefaultPii);
    }
    /**
     * Sends the current Session on the scope
     */

  }, {
    key: "_sendSessionUpdate",
    value: function _sendSessionUpdate() {
      var _this$getStackTop4 = this.getStackTop(),
          scope = _this$getStackTop4.scope,
          client = _this$getStackTop4.client;

      if (!scope) return;
      var session = scope.getSession();

      if (session) {
        if (client && client.captureSession) {
          client.captureSession(session);
        }
      }
    }
    /**
     * Internal helper function to call a method on the top client if it exists.
     *
     * @param method The method to call on the client.
     * @param args Arguments to pass to the client function.
     */

  }, {
    key: "_withClient",
    value: function _withClient(callback) {
      var _this$getStackTop5 = this.getStackTop(),
          scope = _this$getStackTop5.scope,
          client = _this$getStackTop5.client;

      if (client) {
        callback(client, scope);
      }
    }
    /**
     * Calls global extension method and binding current instance to the function call
     */
    // @ts-ignore Function lacks ending return statement and return type does not include 'undefined'. ts(2366)

  }, {
    key: "_callExtensionMethod",
    value: function _callExtensionMethod(method) {
      var carrier = getMainCarrier();
      var sentry = carrier.__SENTRY__;

      if (sentry && sentry.extensions && typeof sentry.extensions[method] === 'function') {
        for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          args[_key - 1] = arguments[_key];
        }

        return sentry.extensions[method].apply(this, args);
      }

      (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.warn("Extension method ".concat(method, " couldn't be found, doing nothing."));
    }
  }]);

  return Hub;
}();
/**
 * Returns the global shim registry.
 *
 * FIXME: This function is problematic, because despite always returning a valid Carrier,
 * it has an optional `__SENTRY__` property, which then in turn requires us to always perform an unnecessary check
 * at the call-site. We always access the carrier through this function, so we can guarantee that `__SENTRY__` is there.
 **/


function getMainCarrier() {
  var carrier = getGlobalObject();
  carrier.__SENTRY__ = carrier.__SENTRY__ || {
    extensions: {},
    hub: undefined
  };
  return carrier;
}
/**
 * Replaces the current main hub with the passed one on the global object
 *
 * @returns The old replaced hub
 */


function makeMain(hub) {
  var registry = getMainCarrier();
  var oldHub = getHubFromCarrier(registry);
  setHubOnCarrier(registry, hub);
  return oldHub;
}
/**
 * Returns the default hub instance.
 *
 * If a hub is already registered in the global carrier but this module
 * contains a more recent version, it replaces the registered version.
 * Otherwise, the currently registered hub will be returned.
 */


function getCurrentHub() {
  // Get main carrier (global for every environment)
  var registry = getMainCarrier(); // If there's no hub, or its an old API, assign a new one

  if (!hasHubOnCarrier(registry) || getHubFromCarrier(registry).isOlderThan(API_VERSION)) {
    setHubOnCarrier(registry, new Hub());
  } // Prefer domains over global if they are there (applicable only to Node environment)


  if (isNodeEnv()) {
    return getHubFromActiveDomain(registry);
  } // Return hub that lives on a global object


  return getHubFromCarrier(registry);
}
/**
 * Try to read the hub from an active domain, and fallback to the registry if one doesn't exist
 * @returns discovered hub
 */


function getHubFromActiveDomain(registry) {
  try {
    var sentry = getMainCarrier().__SENTRY__;

    var activeDomain = sentry && sentry.extensions && sentry.extensions.domain && sentry.extensions.domain.active; // If there's no active domain, just return global hub

    if (!activeDomain) {
      return getHubFromCarrier(registry);
    } // If there's no hub on current domain, or it's an old API, assign a new one


    if (!hasHubOnCarrier(activeDomain) || getHubFromCarrier(activeDomain).isOlderThan(API_VERSION)) {
      var registryHubTopStack = getHubFromCarrier(registry).getStackTop();
      setHubOnCarrier(activeDomain, new Hub(registryHubTopStack.client, Scope.clone(registryHubTopStack.scope)));
    } // Return hub that lives on a domain


    return getHubFromCarrier(activeDomain);
  } catch (_Oo) {
    // Return hub that lives on a global object
    return getHubFromCarrier(registry);
  }
}
/**
 * This will tell whether a carrier has a hub on it or not
 * @param carrier object
 */


function hasHubOnCarrier(carrier) {
  return !!(carrier && carrier.__SENTRY__ && carrier.__SENTRY__.hub);
}
/**
 * This will create a new {@link Hub} and add to the passed object on
 * __SENTRY__.hub.
 * @param carrier object
 * @hidden
 */


function getHubFromCarrier(carrier) {
  return getGlobalSingleton('hub', function () {
    return new Hub();
  }, carrier);
}
/**
 * This will set passed {@link Hub} on the passed object's __SENTRY__.hub attribute
 * @param carrier object
 * @param hub Hub
 * @returns A boolean indicating success or failure
 */


function setHubOnCarrier(carrier, hub) {
  if (!carrier) return false;

  var __SENTRY__ = carrier.__SENTRY__ = carrier.__SENTRY__ || {};

  __SENTRY__.hub = hub;
  return true;
}

export { API_VERSION, Hub, getCurrentHub, getHubFromCarrier, getMainCarrier, makeMain, setHubOnCarrier };